import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Clock, Calendar, Loader2, AlertTriangle, FileCheck2, Brain } from "lucide-react";
import { subjectMeta, fmtDate, fmtDuration, SOURCE_LABEL } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

export const SubjectBadge = ({ subject, className = "" }) => {
  const { user } = useAuth();
  const m = subjectMeta(subject, user?.subject_styles);
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${m.tone} ${className}`}>
      <m.icon className="h-3.5 w-3.5" /> {m.name}
    </span>
  );
};

const StatusLine = ({ c }) => {
  if (c.status === "processing")
    return <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Génération des notes…</span>;
  if (c.status === "recording")
    return <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-600"><AlertTriangle className="h-3.5 w-3.5" /> Enregistrement non terminé</span>;
  if (c.status === "error")
    return <span className="inline-flex items-center gap-1.5 text-xs font-medium text-destructive"><AlertTriangle className="h-3.5 w-3.5" /> Erreur — à relancer</span>;
  return null;
};

export const CourseCard = ({ c, i = 0, onToggleFav, reason }) => (
  <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: Math.min(i, 8) * 0.04 }}>
    <Link to={`/app/cours/${c.course_id}`} data-testid={`course-card-${c.course_id}`} className="card-flat card-hover group flex h-full flex-col p-5">
      <div className="flex items-start justify-between gap-3">
        <SubjectBadge subject={c.subject} />
        <button
          data-testid={`course-fav-${c.course_id}`}
          aria-label="Favori"
          onClick={(e) => { e.preventDefault(); onToggleFav?.(c); }}
          className="rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-amber-500"
        >
          <Star className={`h-4 w-4 ${c.favorite ? "fill-amber-400 text-amber-400" : ""}`} />
        </button>
      </div>
      <h3 className="mt-4 line-clamp-2 text-lg font-bold leading-snug transition-colors group-hover:text-primary">{c.title || "Cours en cours de création"}</h3>
      <p className="mt-2 line-clamp-3 flex-1 text-sm text-muted-foreground">{reason || c.summary || <StatusLine c={c} />}</p>
      <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {fmtDate(c.created_at)}</span>
        {c.duration_sec > 0 && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {fmtDuration(c.duration_sec)}</span>}
        <span>{SOURCE_LABEL[c.source]}</span>
        {c.revision_status === "ready" && <FileCheck2 className="h-3.5 w-3.5 text-primary" aria-label="Fiche disponible" />}
        {c.quiz_status === "ready" && <Brain className="h-3.5 w-3.5 text-violet-500" aria-label="Quiz disponible" />}
      </div>
      {c.summary && c.status !== "ready" && <div className="mt-2"><StatusLine c={c} /></div>}
    </Link>
  </motion.div>
);
